import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { CustomerFormComponent } from '../customer-form/customer-form.component';
import { CustomerService } from '../../../services/customer.service';
import { CustomerCreateRequest } from '../../../models/api/customerCreateRequest';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';

@Component({
  selector: 'app-customer-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CustomerFormComponent, NgxSpinnerModule],
  templateUrl: './customer-create.component.html',
  styleUrls: ['./customer-create.component.scss']
})
export class CustomerCreateComponent {
  @ViewChild(CustomerFormComponent) customerFormComponent?: CustomerFormComponent;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private customerService: CustomerService,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {
    this.form = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required],
      contactPhone1: ['', Validators.required],
      contactPhone2: [''],
      communicationsManager: [''],
      accessStartDate: ['', Validators.required],
      accessEndDate: ['', Validators.required],
      canAccessDirectors: [false],
      isInternal: [false],
      reportVariables: [[]],
      active: [true],
      companies: [[]],
      countries: [[]],
      industries: [[]],
      ipRanges: [[]],
      selectedMeasurements: [[]]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      control.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    const v = this.form.value;
    const request: CustomerCreateRequest = {
      email: v.email,
      fullName: v.fullName,
      userName: v.email,
      password: v.password,
      role: AppUserRole.Customer,
      accessToYears: (v.selectedMeasurements ?? []).map((y: string) => parseInt(y, 10)),
      isActive: !!v.active,
      contactPhone1: v.contactPhone1 ?? '',
      contactPhone2: v.contactPhone2 ?? '',
      communicationsManager: v.communicationsManager ?? '',
      accessStartDate: v.accessStartDate,
      accessEndDate: v.accessEndDate,
      canAccessDirectors: !!v.canAccessDirectors,
      isInternal: !!v.isInternal,
      reportVariableCategoryIds: v.reportVariables ?? [],
      countryIds: v.countries ?? [],
      companyIds: v.companies ?? [],
      industryIds: v.industries ?? [],
      ipRanges: this.customerFormComponent?.ipRanges ?? []
    };

    this.spinnerService.show();
    this.customerService.createCustomer(request).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toast.success('Customer created successfully', {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF'
          }
        });
        this.router.navigate(['/customers']);
      },
      error: (error: Error) => {
        this.spinnerService.hide();
        console.error('Error creating customer:', error);
      }
    });
  }

  onCancel() {
    this.router.navigate(['/customers']);
  }
}
